import React, { useEffect, useState } from "react";
import {
  Form,
  InputNumber,
  Button,
  Space,
  Switch,
  Typography,
  Alert,

} from "antd";
import PropTypes from "prop-types";
import api from "Common/Client/Interceptor";
import { toast } from "react-toastify";

const { Text } = Typography;

const PricingEditor = ({
  courseId,
  initialPricing,
  isPaid,
  hasActiveExpertSupport,
  onSave,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      isPaid,
      price: initialPricing?.price || 0,
      accessDurationDays: initialPricing?.accessDurationDays || 0
    });
  }, [initialPricing, isPaid, form]);

  const handleFinish = async (values) => {
    // Check if trying to change from paid to free with active expert support
    if (isPaid && !values.isPaid && hasActiveExpertSupport) {
      toast.error("Cannot change to free course when expert support is active");
      return;
    }

    setLoading(true);
    try {
      // Payload matching the API response format
      const payload = {
        courseId: parseInt(courseId),
        isPaid: values.isPaid,
        price: values.isPaid ? values.price : 0,
        accessDurationDays: values.isPaid ? values.accessDurationDays : 0
      };

      console.log("pay load for pricing ", payload);
      // Call the API endpoint
      await api.post(`/indv/CoursePricing/unpublished/pricing`, payload);

    
      
      // Show success message
      toast.success("Pricing added/updated successfully");
      
    
      onSave();
    } catch (error) {
      console.error("Error updating pricing:", error);
      toast.error(error.response?.data?.message || "Failed to update pricing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: "white",
        padding: "24px",
        borderRadius: "8px",
        border: "1px solid #f0f0f0",
        marginTop: "12px"
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        {/* Header */}
        <Text strong style={{ fontSize: "16px" }}>Edit Pricing</Text>

        {/* Paid / Free Toggle */}
        <Form.Item
          name="isPaid"
          label="Is this a paid course?"
          valuePropName="checked"
        >
          <Switch 
            disabled={isPaid && hasActiveExpertSupport}
            checkedChildren="Paid"
            unCheckedChildren="Free"
          />
        </Form.Item>

        {/* Warning for expert support */}
        {isPaid && hasActiveExpertSupport && (
          <Alert
            message="Cannot change to free course"
            description="This course has active expert support services. You cannot change a paid course with active services to a free course."
            type="warning"
            showIcon
            style={{ marginBottom: "16px" }}
          />
        )}

        {/* Paid Fields */}
        <Form.Item shouldUpdate>
          {({ getFieldValue }) =>
            getFieldValue("isPaid") && (
              <>
                <Form.Item
                  name="price"
                  label="Price"
                  rules={[
                    { required: true, message: "Please enter course price" },
                    { type: "number", min: 0, message: "Price must be at least 0" }
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    style={{ width: "100%" }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    placeholder="0.00"
                  />
                </Form.Item>

                <Form.Item
                  name="accessDurationDays"
                  label="Access Duration (Days)"
                  rules={[
                    { required: true, message: "Please enter access duration" },
                    { type: "number", min: 1, message: "Access duration must be at least 1 day" }
                  ]}
                >
                  <InputNumber
                    min={1}
                    style={{ width: "100%" }}
                    placeholder="Number of days"
                  />
                </Form.Item>
              </>
            )
          }
        </Form.Item>

        {/* Actions */}
        <Space style={{ marginTop: "16px" }}>
          <Button 
            type="primary" 
            htmlType="submit"
            loading={loading}
          >
            Save
          </Button>
          <Button onClick={onCancel}>
            Cancel
          </Button>
        </Space>
      </Form>
    </div>
  );
};

PricingEditor.propTypes = {
  courseId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  isPaid: PropTypes.bool.isRequired,
  hasActiveExpertSupport: PropTypes.bool,
  initialPricing: PropTypes.shape({
    price: PropTypes.number,
    accessDurationDays: PropTypes.number
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default PricingEditor;